import {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import {getErrorMessage} from '~/common/utils/errors';
import {CLEAR_FEATURED_PHOTO, FEATURE_PHOTO} from '~/ViewInvitation/reducers/constants';
import {Photo} from '~/ViewInvitation/types/photoShare';
import {toastAlert} from '~library/atoms/Toast';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

interface UseFeaturePhotoProps {
  isHostModule: boolean;
  doFeature: (photo: Photo, isStaged: boolean) => Promise<void>;
  setPhotoToDelete: (id: string | number | null) => void;
  setShowingContextMenu: (show: boolean) => void;
}

interface PhotoToFeature extends Photo {
  isStaged?: boolean;
  src?: string | null;
  position?: number | null;
  isFeatured?: boolean | null;
}

interface UseFeaturePhotoReturn {
  photoToFeature: PhotoToFeature;
  showingFeatureMessage: boolean;
  handleFeaturePhoto: (
    photo: Photo,
    isStaged: boolean,
    src?: string | null,
    isFeatured?: boolean
  ) => void;
  confirmFeaturePhoto: () => Promise<void>;
  handleCloseFeatureConfirm: () => void;
}

export const useFeaturePhoto = ({
  isHostModule,
  doFeature,
  setPhotoToDelete,
  setShowingContextMenu,
}: UseFeaturePhotoProps): UseFeaturePhotoReturn => {
  const dispatch = useDispatch();
  const isLargishAndUp = useIsLargishAndUp();

  const [photoToFeature, setPhotoToFeature] = useState<PhotoToFeature>({id: ''});
  const [showingFeatureMessage, setShowingFeatureMessage] = useState<boolean>(false);

  const handleFeaturePhoto = useCallback(
    (photo: Photo, isStaged: boolean, src: string | null = null, isFeatured: boolean = false) => {
      setPhotoToFeature({
        ...photo,
        isStaged,
        src,
        position: isHostModule ? 1 : null,
        isFeatured: isHostModule ? isFeatured : null,
      });
      setPhotoToDelete(photo.id);
      if (isLargishAndUp) {
        setShowingFeatureMessage(true);
      } else {
        setShowingContextMenu(true);
      }
    },
    [isLargishAndUp, isHostModule, setPhotoToDelete, setShowingContextMenu]
  );

  const confirmFeaturePhoto = useCallback(async () => {
    try {
      const {isStaged} = photoToFeature;
      await doFeature(photoToFeature, !!isStaged);
      dispatch({type: CLEAR_FEATURED_PHOTO});
      if (isStaged) {
        dispatch({
          type: FEATURE_PHOTO,
          payload: photoToFeature,
        });
      }
      setShowingFeatureMessage(false);
      setShowingContextMenu(false);
    } catch (e: unknown) {
      const errorMessage = getErrorMessage(e);
      toastAlert(`Error uploading photos: ${errorMessage}`, {type: 'error'});
    }
  }, [dispatch, photoToFeature, doFeature, setShowingContextMenu]);

  const handleCloseFeatureConfirm = useCallback(() => {
    if (photoToFeature.isStaged) {
      dispatch({type: CLEAR_FEATURED_PHOTO});
    }
    setShowingFeatureMessage(false);
  }, [photoToFeature, dispatch]);

  return {
    photoToFeature,
    showingFeatureMessage,
    handleFeaturePhoto,
    confirmFeaturePhoto,
    handleCloseFeatureConfirm,
  };
};
